import { z } from "zod";

import type { GetScimLogSearchServiceFrontendResponse } from "@performyard/service-contracts";

import { ScimLogsFilterSchema } from "../../../../pages/Administration/SCIMSettings/ScimLogs/components/ScimFilterPanel";

import request from "./internal-base-request";

const GetScimLogsParamsSchema = z.object({
	scimToken: z.string().optional(),
	scimId: z.string().optional(),
	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: z.string(),
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
	userEmail: z.string().optional(),
	filters: ScimLogsFilterSchema.optional(),
});

export type GetScimLogsParams = z.infer<typeof GetScimLogsParamsSchema>;

// Column names need to be mapped to search service property names
const sortMap: Record<string, string> = {
	assignedDate: "changed_at",
	subject: "subject_name",
};

export const getScimLogs = async (params: GetScimLogsParams): Promise<GetScimLogSearchServiceFrontendResponse> => {
	const validatedParams = GetScimLogsParamsSchema.parse(params);
	const baseUrl = "/v1/search";
	const searchParams = new URLSearchParams();

	searchParams.append("service", "scim_log");

	// Function to append each array parameter to the query
	const appendParam = ({
		key,
		queryType,
		value,
	}: {
		key: string;
		queryType?: "eq" | "re" | "lt" | "in";
		value: string;
	}) => {
		if (value) {
			if (queryType) {
				searchParams.append(`${key}_${queryType}`, value);
			} else {
				searchParams.append(key, value);
			}
		}
	};

	// If custom sort provided, map sort parameter and append to query
	// Otherwise, default to assigned date descending
	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = sortMap[name] || name;
		appendParam({
			key: "sort",
			value: `${direction}(${sortName})`,
		});
	} else {
		const defaultSort = "desc(changed_at)";
		appendParam({
			key: "sort",
			value: defaultSort,
		});
	}

	// If skip and/or limit provided, append to query
	if (validatedParams.limit) {
		appendParam({
			key: "limit",
			value: validatedParams.limit.toString(),
		});
	}

	if (validatedParams.skip) {
		appendParam({
			key: "skip",
			value: validatedParams.skip.toString(),
		});
	}

	// If user email search term provided, append to query
	if (validatedParams.userEmail) {
		appendParam({
			key: "user_email",
			queryType: "re",
			value: validatedParams.userEmail,
		});
	}

	// If filters provided, append to query
	if (validatedParams.filters) {
		if (validatedParams.filters.type) {
			for (const type of validatedParams.filters.type) {
				appendParam({
					key: "type",
					queryType: "in",
					value: type.value,
				});
			}
		}

		if (validatedParams.filters.status) {
			for (const status of validatedParams.filters.status) {
				appendParam({
					key: "status",
					queryType: "in",
					value: status.value,
				});
			}
		}

		if (validatedParams.filters.date && Number.parseInt(validatedParams.filters.date.value, 10)) {
			appendParam({
				key: "day_count",
				queryType: "lt",
				value: validatedParams.filters.date.value,
			});
		}
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [scimLogsResponse] = await request<GetScimLogSearchServiceFrontendResponse>(`${baseUrl}${queryString}`);

	// Create formatted response with timestamp strings converted to date objects
	const formattedResponse: GetScimLogSearchServiceFrontendResponse = {
		errors: scimLogsResponse.errors ?? [],
		warnings: scimLogsResponse.warnings ?? [],
		total: scimLogsResponse.total ?? 0,
		results:
			scimLogsResponse.results?.map((log) => ({
				...log,
				timestamp: new Date(log.timestamp),
			})) ?? [],
	};

	return formattedResponse;
};
